import SiteCoreLinks from "@/components/SiteCoreLinks";
// /tax — TH hub. Phase P-TAX-DEEP-DIVE.
import { createFileRoute, Link } from "@tanstack/react-router";
import { DTA_COUNTRIES } from "@/data/tax-dta";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, itemListSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Sparkles, ArrowRight, FileText, Globe2, Calculator } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/tax";

const HUB_FAQS = [
  { q: "ใครต้องเสียภาษีเงินได้ต่างประเทศในไทย", a: "ผู้ที่อยู่ในไทย ≥ 180 วันต่อปีปฏิทิน (Tax Resident ตาม ป.รัษฎากร มาตรา 41 วรรค 3) ที่นำเงินได้ต่างประเทศ (เงินเดือน, dividend, capital gain, royalty, ค่าเช่า) เข้าไทยตั้งแต่ 1 ม.ค. 2567 ตามคำสั่งกรมสรรพากรที่ Por.161/2566 แก้ไขกฎเดิมที่อ้างหลัก remittance year-of-source" },
  { q: "Worldwide Income 2024 คืออะไร", a: "Por.161/2566 + Por.162/2566 เปลี่ยนกฎภาษีเงินได้ต่างประเทศตาม ป.รัษฎากร ม.41 — ปีไหนนำเข้าไทย ปีนั้นเสียภาษีอัตราก้าวหน้า 0–35% ไม่ขึ้นกับปีที่หาเงินได้ ใช้ Foreign Tax Credit ตาม DTA หลีกเลี่ยงภาษีซ้ำ" },
  { q: "LTR Visa ได้รับยกเว้นภาษีไหม", a: "ได้ — พระราชกฤษฎีกา 743/2565 มาตรา 4 ยกเว้นภาษีเงินได้ต่างประเทศที่นำเข้าไทยให้ผู้ถือ LTR 3 ประเภท: Wealthy Global Citizen (WGC), Wealthy Pensioner (WP), และ Work-from-Thailand Professional (WFTP) — Highly-Skilled Professional (HSP) เสียภาษี Flat 17%" },
  { q: "ไทยมี DTA กับกี่ประเทศ", a: "61 ประเทศ ครอบคลุม OECD Model Article 10 (Dividend), 11 (Interest), 12 (Royalty), 14 (Service Fee) — รวมประเทศหลัก: สหรัฐฯ, สหราชอาณาจักร, ออสเตรเลีย, ญี่ปุ่น, สิงคโปร์, เยอรมัน, จีน — เพื่อหลีกเลี่ยงภาษีซ้ำซ้อนและ tax credit relief" },
  { q: "ยื่นภาษีเมื่อไร", a: "ภ.ง.ด.90/91 ภายใน 31 มีนาคมปีถัดไป (e-Filing ผ่าน rd.go.th ขยายถึง 8 เมษายน) — ครึ่งปี ภ.ง.ด.94 ภายใน 30 กันยายน — หัก ณ ที่จ่าย ภ.ง.ด.1 ภายในวันที่ 7 ของเดือนถัดไป" },

];

type TaxCard = { slug: string; titleTh: string; taglineTh: string };

export const Route = createFileRoute("/tax/")({
  loader: async (): Promise<{ topics: TaxCard[] }> => {
    const { TAX_TOPICS } = await import("@/data/tax-topics");
    return { topics: TAX_TOPICS.map((t) => ({ slug: t.slug, titleTh: t.titleTh, taglineTh: t.taglineTh })) };
  },
  head: ({ loaderData }) => {
    const TAX_TOPICS: TaxCard[] = loaderData?.topics ?? [];
    const title = "ภาษีไทยสำหรับชาวต่างชาติและคนไทย 2026 — Worldwide Income, DTA 61 ประเทศ | NYC";
    const desc = "คู่มือภาษีฉบับสมบูรณ์: Worldwide Income 2024, Foreign Tax Credit, DTA 61 ประเทศ, LTR Tax Exemption, ยื่น ภ.ง.ด.90/91 — โดย NYC Legal & Notary — ปรึกษาฟรี ประเมินงานให้ทันที ทักไลน์ได้เลย";
    return {
      meta: [
        { title: ctaTitle(title, "/tax", "th") }, { name: "description", content: ctaDesc(desc, "/tax", "th")},
        { property: "og:title", content: ctaTitle(title, "/tax", "th")}, { property: "og:description", content: ctaDesc(desc, "/tax", "th")},
        { property: "og:url", content: `${SITE}${PATH}` }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: `${SITE}${PATH}` },
        ...hreflangLinks(PATH, "/en/tax"),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          itemListSchema({
            name: "หัวข้อภาษีไทย",
            items: TAX_TOPICS.map((t) => ({ name: t.titleTh, url: `${SITE}${PATH}/${t.slug}` })),
          }),
          itemListSchema({
            name: "DTA ของไทย",
            items: DTA_COUNTRIES.slice(0, 30).map((d) => ({ name: d.countryTh, url: `${SITE}${PATH}/dta/${d.slug}` })),
          }),
          faqPageSchema(HUB_FAQS),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "ภาษีไทย", item: `${SITE}${PATH}` },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: TaxHub,
});

function TaxHub() {
  const { topics: TAX_TOPICS } = Route.useLoaderData();
  return (
    <main className="min-h-screen bg-background text-foreground">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-5xl">
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">เนื้อหาเชิงลึก — ปรับปรุง 2026</span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">ภาษีไทยฉบับสมบูรณ์ — Worldwide Income, DTA, LTR</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            คู่มือภาษีเชิงลึกสำหรับชาวต่างชาติและคนไทย: กฎ Worldwide Income 2024 (Por.161/162), Foreign Tax Credit ตาม DTA 61 ประเทศ, LTR Tax Exemption, การยื่น ภ.ง.ด.90/91 และข้อพิจารณาเฉพาะ Crypto, Capital Gains, WHT
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">ระบบภาษีเงินได้บุคคลธรรมดาไทย 2026 ทำงานอย่างไร?</h2>
            <p className="direct-answer mt-2 text-sm leading-relaxed sm:text-base">
              ผู้มีถิ่นที่อยู่ในไทย (≥180 วัน/ปี) ต้องเสียภาษีอัตราก้าวหน้า 0–35% บนเงินได้ในไทย และเงินได้ต่างประเทศที่นำเข้าไทย (Worldwide Income 2024) ใช้ Foreign Tax Credit ตาม DTA 61 ประเทศได้ ผู้ถือ LTR Visa 3 ประเภทได้รับยกเว้นภาษีเงินได้ต่างประเทศตามพระราชกฤษฎีกา 743
            </p>
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <h2 className="flex items-center gap-2 text-2xl font-extrabold sm:text-3xl"><Calculator className="h-6 w-6 text-primary" /> หัวข้อภาษีเชิงลึก</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {TAX_TOPICS.map((t: TaxCard) => (
              <Link key={t.slug} to="/tax/$topic" params={{ topic: t.slug }} className="group rounded-2xl border border-border bg-card p-5 hover:border-primary">
                <h3 className="text-lg font-bold group-hover:text-primary">{t.titleTh}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{t.taglineTh}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary">อ่านต่อ <ArrowRight className="h-3 w-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DTA directory */}
      <section className="bg-muted/30 px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <h2 className="flex items-center gap-2 text-2xl font-extrabold sm:text-3xl"><Globe2 className="h-6 w-6 text-primary" /> DTA ของไทย ({DTA_COUNTRIES.length} ประเทศ)</h2>
          <p className="mt-3 max-w-3xl text-muted-foreground">ดูอัตรา Dividend / Interest / Royalty WHT และวิธีใช้ Foreign Tax Credit สำหรับแต่ละสนธิสัญญา</p>
          <div className="mt-6 grid gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {DTA_COUNTRIES.map((d) => (
              <Link key={d.slug} to="/tax/dta/$country" params={{ country: d.slug }} className="rounded-lg border border-border bg-card p-3 text-sm hover:border-primary">
                <span className="font-bold">{d.countryTh}</span>
                <div className="mt-1 text-[11px] text-muted-foreground">Div {d.whtDividend} · Int {d.whtInterest}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 py-14">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-6 space-y-3">
            {HUB_FAQS.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-4">
                <summary className="cursor-pointer font-bold">{f.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer />
          <SiteCoreLinks />
    </main>
  );
}
